import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { cn } from "@/design-system/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { useInvestorList, useInvestorCommitments } from "@/hooks/capitalMarkets/useInvestors";
import { useInvestorDistributions } from "@/hooks/capitalMarkets/useInvestors";
import {
  useEntityNameMap,
  usePersonNameMap,
  resolveInvestorName,
} from "@/hooks/capitalMarkets/useCounterpartyNames";
import {
  INVESTOR_STATUSES,
  INVESTOR_TYPES,
  type InvestorMaskedDto,
} from "@/types/investors";
import {
  InvestorStatusBadge,
  VerificationChip,
  KpiCard,
} from "../shared/components";
import { downloadCsv, formatCurrency, formatDate, humanizeStatus } from "../shared/format";
import { NewInvestorModal } from "./NewInvestorModal";

interface InvestorAgg {
  activeCommitments: number;
  totalCommitted: number;
  totalFunded: number;
  lastDistribution: string | null;
}

const ACTIVE_COMMITMENT_STATUSES = new Set(["Indication", "Committed", "Funded"]);

export function InvestorListPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const tz = useUserTimeZone();
  const canManage = hasPermission("Investor.Manage");

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [accreditationFilter, setAccreditationFilter] = useState<string>("");
  const [kycFilter, setKycFilter] = useState<string>("");
  const [showNew, setShowNew] = useState(false);

  const investorsQuery = useInvestorList({ pageSize: 200 });
  const commitmentsQuery = useInvestorCommitments({ pageSize: 500 });
  const distributionsQuery = useInvestorDistributions({ pageSize: 500 });
  const { map: personNames } = usePersonNameMap();
  const { map: entityNames } = useEntityNameMap();

  const investors = useMemo(
    () => investorsQuery.data?.data ?? [],
    [investorsQuery.data],
  );

  // Aggregate commitments + distributions per investor. The masked Investor
  // DTO carries no cross-pool rollups, so the list computes them client-side
  // from the (paged) commitment + distribution directories.
  const aggregates = useMemo(() => {
    const commitments = commitmentsQuery.data?.data ?? [];
    const distributions = distributionsQuery.data?.data ?? [];
    const commitmentToInvestor = new Map<string, string>();
    const agg = new Map<string, InvestorAgg>();
    const ensure = (id: string) => {
      let a = agg.get(id);
      if (!a) {
        a = { activeCommitments: 0, totalCommitted: 0, totalFunded: 0, lastDistribution: null };
        agg.set(id, a);
      }
      return a;
    };
    for (const c of commitments) {
      commitmentToInvestor.set(c.id, c.investorId);
      const a = ensure(c.investorId);
      a.totalCommitted += c.committedAmount;
      a.totalFunded += c.fundedAmount;
      if (ACTIVE_COMMITMENT_STATUSES.has(c.status)) a.activeCommitments += 1;
    }
    for (const d of distributions) {
      if (!d.paidAt) continue;
      const investorId = commitmentToInvestor.get(d.investorCommitmentId);
      if (!investorId) continue;
      const a = ensure(investorId);
      if (!a.lastDistribution || d.paidAt > a.lastDistribution) {
        a.lastDistribution = d.paidAt;
      }
    }
    return agg;
  }, [commitmentsQuery.data, distributionsQuery.data]);

  const rows = useMemo(() => {
    return investors.filter((inv) => {
      if (statusFilter && inv.status !== statusFilter) return false;
      if (typeFilter && inv.investorType !== typeFilter) return false;
      if (accreditationFilter === "verified" && !inv.accreditationVerifiedAt) return false;
      if (accreditationFilter === "pending" && inv.accreditationVerifiedAt) return false;
      if (kycFilter === "verified" && !inv.amlKycVerifiedAt) return false;
      if (kycFilter === "pending" && inv.amlKycVerifiedAt) return false;
      return true;
    });
  }, [investors, statusFilter, typeFilter, accreditationFilter, kycFilter]);

  const nameOf = (inv: InvestorMaskedDto) =>
    resolveInvestorName(inv.personId, inv.entityId, personNames, entityNames);

  const kpis = useMemo(() => {
    const active = investors.filter((i) => i.status === "Active").length;
    let committed = 0;
    for (const a of aggregates.values()) committed += a.totalCommitted;
    return { count: investors.length, active, committed };
  }, [investors, aggregates]);

  const columns: Column<InvestorMaskedDto>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        render: (i) => (
          <Link
            to={`/capital-markets/investors/${i.id}`}
            className="font-semibold hover:text-gold-shadow"
          >
            {nameOf(i)}
          </Link>
        ),
      },
      {
        key: "type",
        header: "Type",
        render: (i) => humanizeStatus(i.investorType),
      },
      {
        key: "status",
        header: "Status",
        render: (i) => <InvestorStatusBadge status={i.status} />,
      },
      {
        key: "accreditation",
        header: "Accreditation",
        render: (i) => <VerificationChip verifiedAt={i.accreditationVerifiedAt} />,
      },
      {
        key: "kyc",
        header: "KYC",
        render: (i) => <VerificationChip verifiedAt={i.amlKycVerifiedAt} />,
      },
      {
        key: "activeCommitments",
        header: "# Active",
        render: (i) => aggregates.get(i.id)?.activeCommitments ?? 0,
      },
      {
        key: "committed",
        header: "Total Committed",
        render: (i) => formatCurrency(aggregates.get(i.id)?.totalCommitted ?? 0),
      },
      {
        key: "funded",
        header: "Total Funded",
        render: (i) => formatCurrency(aggregates.get(i.id)?.totalFunded ?? 0),
      },
      {
        key: "lastDistribution",
        header: "Last Distribution",
        render: (i) => formatDate(aggregates.get(i.id)?.lastDistribution),
      },
      {
        key: "actions",
        header: "",
        className: "w-10 text-right",
        render: (i) => (
          <Link
            to={`/capital-markets/investors/${i.id}`}
            className="text-caption text-gold-shadow font-display uppercase tracking-wide hover:underline"
          >
            View
          </Link>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aggregates, personNames, entityNames],
  );

  const handleExport = () => {
    const header = [
      "Name",
      "Type",
      "Status",
      "Accreditation",
      "KYC",
      "# Active Commitments",
      "Total Committed",
      "Total Funded",
      "Last Distribution",
    ];
    const csv = rows.map((i) => {
      const a = aggregates.get(i.id);
      return [
        nameOf(i),
        humanizeStatus(i.investorType),
        humanizeStatus(i.status),
        i.accreditationVerifiedAt ? "Verified" : "Pending",
        i.amlKycVerifiedAt ? "Verified" : "Pending",
        String(a?.activeCommitments ?? 0),
        String(a?.totalCommitted ?? 0),
        String(a?.totalFunded ?? 0),
        a?.lastDistribution ?? "",
      ];
    });
    downloadCsv(
      `investors-${isoDateInTz(new Date(), tz)}.csv`,
      [header, ...csv],
    );
  };

  return (
    <>
      <PageHeader
        eyebrow="Capital Markets"
        title="Investors"
        description="Reusable accredited-investor master records and their commitments."
        actions={
          <div className="flex items-center gap-space-3">
            <Button variant="secondary" size="sm" onClick={handleExport}>
              <Download size={14} strokeWidth={1.5} aria-hidden /> Export CSV
            </Button>
            {canManage && (
              <Button onClick={() => setShowNew(true)}>
                <Plus size={16} strokeWidth={2} aria-hidden /> New Investor
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3 mb-space-4">
        <KpiCard label="Total Investors" value={kpis.count} />
        <KpiCard label="Active Investors" value={kpis.active} />
        <KpiCard
          label="Aggregate Committed"
          value={formatCurrency(kpis.committed)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-space-2 mb-space-3">
        <FilterChip label="All" active={statusFilter === null} onClick={() => setStatusFilter(null)} />
        {INVESTOR_STATUSES.map((s) => (
          <FilterChip
            key={s}
            label={humanizeStatus(s)}
            active={statusFilter === s}
            onClick={() => setStatusFilter(s)}
          />
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-space-2">
          <Select
            aria-label="Filter by type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            {INVESTOR_TYPES.map((t) => (
              <option key={t} value={t}>
                {humanizeStatus(t)}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filter by accreditation"
            value={accreditationFilter}
            onChange={(e) => setAccreditationFilter(e.target.value)}
          >
            <option value="">Accreditation: any</option>
            <option value="verified">Accredited</option>
            <option value="pending">Not verified</option>
          </Select>
          <Select
            aria-label="Filter by KYC"
            value={kycFilter}
            onChange={(e) => setKycFilter(e.target.value)}
          >
            <option value="">KYC: any</option>
            <option value="verified">KYC verified</option>
            <option value="pending">KYC pending</option>
          </Select>
        </div>
      </div>

      <div
        role="presentation"
        onClick={(e) => {
          const tr = (e.target as HTMLElement).closest("tr[data-investor-id]");
          if (tr)
            navigate(
              `/capital-markets/investors/${tr.getAttribute("data-investor-id")}`,
            );
        }}
      >
        <DataTable
          columns={columns}
          rows={rows}
          loading={investorsQuery.isLoading}
          error={investorsQuery.isError ? "Could not load investors." : null}
          emptyMessage="No investors yet. Create your first investor record to begin."
          rowKey={(i) => i.id}
        />
      </div>

      {showNew && (
        <NewInvestorModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            navigate(`/capital-markets/investors/${id}`);
          }}
        />
      )}
    </>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-space-3 py-space-1 rounded-full text-caption font-display uppercase tracking-wide border",
        active
          ? "bg-gold text-onyx border-gold font-semibold"
          : "bg-white-pure text-gray-mid border-border-light hover:text-onyx",
      )}
    >
      {label}
    </button>
  );
}
